import React, { useState, useCallback } from 'react'
import axios from 'axios'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import CompleteCycleDialog from './CompleteCycleDialog'
import { tokens } from './v4Styles'

const ASPIRE_BASE_URL = process.env.REACT_APP_ASPIRE_BASE_URL || 'https://cloud.youraspire.com'
const ASPIRE_RECEIPT_URL_TEMPLATE = process.env.REACT_APP_ASPIRE_RECEIPT_URL_TEMPLATE || `${ASPIRE_BASE_URL}/app/purchasing/receipts/details/{receiptId}`
const ENABLE_AUTOMATED_INVOICE_APPROVAL = process.env.REACT_APP_HERBICIDE_AUTO_APPROVE_INVOICES === 'true'

const buildAspireReceiptUrl = (receiptId) => {
  if (!receiptId) return null
  return ASPIRE_RECEIPT_URL_TEMPLATE.replace('{receiptId}', encodeURIComponent(receiptId))
}

const buildAspireWorkTicketUrl = (workTicketId) => {
  if (!workTicketId) return null
  return `${ASPIRE_BASE_URL}/app/worktickets/details/${workTicketId}`
}

const formatDate = (val) => {
  if (!val) return '\u2014'
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatCurrency = (val) => {
  if (val == null || val === '') return '\u2014'
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatTimeAgo = (date) => {
  if (!date) return ''
  const ms = Date.now() - new Date(date).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const categoryLabels = {
  needs_review: 'NEEDS REVIEW',
  sub_invoices: 'INVOICE',
  geometry_reconciliation: 'GEOMETRY',
  aspire_tickets: 'ASPIRE TICKET',
  field_issues: 'FIELD ISSUE',
  stale: 'STALE',
  unread_messages: 'UNREAD MESSAGE'
}

const categoryColors = {
  needs_review: '#FF9800',
  sub_invoices: '#2e7d32',
  geometry_reconciliation: '#00695c',
  aspire_tickets: '#7b1fa2',
  field_issues: '#c62828',
  stale: '#e65100',
  unread_messages: '#5d4037'
}

const NeedsReviewDetail = ({ data }) => (
  <>
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 12, color: '#333' }}>Spray Coverage</div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>{data.scopeMode === 'all' ? 'All' : 'Selected'}</div>
          <div style={{ fontSize: 10, color: '#999' }}>Scope</div>
        </div>
        {data.totalAcres && (
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>{data.totalAcres}</div>
            <div style={{ fontSize: 10, color: '#999' }}>Acres</div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 10, color: '#999' }}>Submitted {formatTimeAgo(data.submittedAt)}</div>
    </div>
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Details</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
        <div><span style={{ color: '#999' }}>Last Applied:</span> <span style={{ color: '#333' }}>{formatDate(data.lastAppliedDate)}</span></div>
        <div><span style={{ color: '#999' }}>Next Scheduled:</span> <span style={{ color: '#333' }}>{formatDate(data.nextScheduledDate)}</span></div>
        <div><span style={{ color: '#999' }}>Contract:</span> <span style={{ color: data.hasHerbicideContract ? '#2e7d32' : '#e65100' }}>{data.hasHerbicideContract ? 'Recurring' : 'One-Time'}</span></div>
        <div><span style={{ color: '#999' }}>Blue Dye:</span> <span style={{ color: '#333' }}>{data.blueDyePreference || 'Unspecified'}</span></div>
      </div>
    </div>
  </>
)

const AspireTicketDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: '#333' }}>Aspire Ticket</div>
      {data.workTicketId && (
        <a
          href={`${ASPIRE_BASE_URL}/app/worktickets/details/${data.workTicketId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 10, color: '#7b1fa2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}
        >
          Open in Aspire <OpenInNewIcon style={{ fontSize: 12 }} />
        </a>
      )}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
      <div><span style={{ color: '#999' }}>Ticket:</span> {data.workTicketId ? (
        <a href={`${ASPIRE_BASE_URL}/app/worktickets/details/${data.workTicketId}`} target="_blank" rel="noopener noreferrer" style={{ color: '#7b1fa2', fontWeight: 600, textDecoration: 'none' }}>{data.workTicketNumber || data.workTicketId}</a>
      ) : (
        <span style={{ color: '#333', fontWeight: 600 }}>{data.workTicketNumber || '\u2014'}</span>
      )}</div>
      <div><span style={{ color: '#999' }}>Status:</span> <span style={{ color: '#333' }}>{data.status || '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Hours Actual:</span> <span style={{ color: '#333' }}>{data.hoursActual ?? '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Hours Estimated:</span> <span style={{ color: '#333' }}>{data.hoursEstimated ?? '\u2014'}</span></div>
      <div><span style={{ color: '#999' }}>Scheduled:</span> <span style={{ color: '#333' }}>{formatDate(data.scheduledDate)}</span></div>
      <div><span style={{ color: '#999' }}>Last Visit:</span> <span style={{ color: '#333' }}>{formatDate(data.latestVisitDate)}</span></div>
      {data.crewLeaderName && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#999' }}>Crew Leader:</span> <span style={{ color: '#333' }}>{data.crewLeaderName}</span></div>}
    </div>
  </div>
)

const SubInvoiceDetail = ({ data }) => {
  const receipt = data.aspireReceipt || {}
  const workTicket = data.workTicket || {}
  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: '#333' }}>Subcontractor Invoice</div>
        {data.invoicePdfUrl && (
          <a
            href={data.invoicePdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 10, color: '#2e7d32', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}
          >
            Invoice PDF <OpenInNewIcon style={{ fontSize: 12 }} />
          </a>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
        <div><span style={{ color: '#999' }}>Invoice:</span> <span style={{ color: '#333', fontWeight: 600 }}>{data.invoiceNumber || '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Status:</span> <span style={{ color: data.invoiceStatus === 'approved' ? '#2e7d32' : '#e65100', fontWeight: 600 }}>{data.invoiceStatus || '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Amount:</span> <span style={{ color: '#333' }}>{formatCurrency(data.finalAmount ?? data.amount)}</span></div>
        <div><span style={{ color: '#999' }}>Acreage:</span> <span style={{ color: '#333' }}>{data.acreage ?? '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Vendor:</span> <span style={{ color: '#333' }}>{data.vendorName || '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Created:</span> <span style={{ color: '#333' }}>{formatDate(data.createdAt)}</span></div>
        <div><span style={{ color: '#999' }}>Receipt:</span> <span style={{ color: receipt.receiptId ? '#2e7d32' : '#c62828', fontWeight: 600 }}>{receipt.receiptId ? `#${receipt.receiptId}` : 'Not pushed'}</span></div>
        <div><span style={{ color: '#999' }}>Receipt Approval:</span> <span style={{ color: receipt.approvedAt ? '#2e7d32' : '#e65100', fontWeight: 600 }}>{receipt.approvedAt ? formatDate(receipt.approvedAt) : 'Needs approval'}</span></div>
        {workTicket.workTicketId && (
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={{ color: '#999' }}>Work Ticket:</span>{' '}
            <a href={`${ASPIRE_BASE_URL}/app/worktickets/details/${workTicket.workTicketId}`} target="_blank" rel="noopener noreferrer" style={{ color: '#7b1fa2', fontWeight: 600, textDecoration: 'none' }}>
              {workTicket.workTicketNumber || workTicket.workTicketId}
            </a>
          </div>
        )}
      </div>
      {data.notes && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: '#f5f7f8', color: '#455a64', fontSize: 12, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
          {data.notes}
        </div>
      )}
      {receipt.error && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: '#ffebee', color: '#c62828', fontSize: 11, lineHeight: 1.4 }}>
          Aspire receipt push error: {receipt.error}
        </div>
      )}
      {receipt.approvalError && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: '#ffebee', color: '#c62828', fontSize: 11, lineHeight: 1.4 }}>
          Aspire receipt approval error: {receipt.approvalError}
        </div>
      )}
      {receipt.receiptId && !receipt.approvedAt && (
        <div style={{ marginTop: 12, color: '#607d8b', fontSize: 12, lineHeight: 1.4 }}>
          Review and approve the Aspire receipt, then complete the work ticket manually in Aspire.
        </div>
      )}
    </div>
  )
}

const FieldIssueDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Issue Report</div>
    <div style={{ fontSize: 13, color: '#333', marginBottom: 8, lineHeight: 1.5 }}>{data.issueText || 'No description'}</div>
    {data.photos?.length > 0 && (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {data.photos.map((url, i) => (
          <img key={i} src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
        ))}
      </div>
    )}
    <div style={{ fontSize: 10, color: '#999' }}>
      Reported by {data.reportedBy} &middot; {formatTimeAgo(data.reportedAt)}
    </div>
  </div>
)

const StaleDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Stale Job</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
      <div><span style={{ color: '#999' }}>Queue Status:</span> <span style={{ color: '#e65100', fontWeight: 600 }}>{data.queueStatus === 'partial' ? 'Partial' : 'In Progress'}</span></div>
      <div><span style={{ color: '#999' }}>Days Stale:</span> <span style={{ color: '#c62828', fontWeight: 600 }}>{data.daysSinceActivity || '?'} days</span></div>
      <div><span style={{ color: '#999' }}>Last Activity:</span> <span style={{ color: '#333' }}>{formatDate(data.lastActivityDate)}</span></div>
    </div>
  </div>
)

const UnreadMessageDetail = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Message</div>
    <div style={{ fontSize: 13, color: '#333', marginBottom: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
      {data.messageText || 'No message body'}
    </div>
    <div style={{ fontSize: 10, color: '#999' }}>
      From {data.senderName} &middot; {formatTimeAgo(data.sentAt)}
    </div>
  </div>
)

const GeometryReconciliationDetail = ({ data }) => {
  const event = data.event || {}
  const geometry = event.sprayGeometryId || {}
  const area = event.areaId || {}
  const flags = event.reviewFlags || []
  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: '#333' }}>Geometry Review</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11, marginBottom: 12 }}>
        <div><span style={{ color: '#999' }}>Action:</span> <span style={{ color: '#333' }}>{String(event.action || '').replace(/_/g, ' ')}</span></div>
        <div><span style={{ color: '#999' }}>Status:</span> <span style={{ color: '#333' }}>{event.status || '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Severity:</span> <span style={{ color: geometry.validationSeverity === 'quarantine' ? '#e65100' : '#333' }}>{geometry.validationSeverity || '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Area:</span> <span style={{ color: '#333' }}>{area.name || geometry.proposalGroupLabel || geometry.fieldRootKey || '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>SF:</span> <span style={{ color: '#333' }}>{geometry.areaSF ? Math.round(geometry.areaSF).toLocaleString() : '\u2014'}</span></div>
        <div><span style={{ color: '#999' }}>Created:</span> <span style={{ color: '#333' }}>{formatDate(event.createdAt)}</span></div>
      </div>
      {flags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {flags.map(flag => (
            <span key={flag} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 10, background: '#fff3e0', color: '#e65100' }}>
              {flag.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
      {event.note && (
        <div style={{ fontSize: 12, color: '#455a64', lineHeight: 1.4 }}>{event.note}</div>
      )}
    </div>
  )
}

const detailRenderers = {
  needs_review: NeedsReviewDetail,
  sub_invoices: SubInvoiceDetail,
  geometry_reconciliation: GeometryReconciliationDetail,
  aspire_tickets: AspireTicketDetail,
  field_issues: FieldIssueDetail,
  stale: StaleDetail,
  unread_messages: UnreadMessageDetail
}

const InvoiceManualApprovalDialog = ({ open, item, onClose }) => {
  const receiptId = item?.data?.aspireReceipt?.receiptId
  const workTicketId = item?.data?.workTicket?.workTicketId
  const receiptUrl = buildAspireReceiptUrl(receiptId)
  const workTicketUrl = buildAspireWorkTicketUrl(workTicketId)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Invoice Review</DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{ color: tokens.text.secondary, marginBottom: 14, lineHeight: 1.45 }}>
          Review the Aspire receipt, approve it in Aspire, then complete the work ticket manually.
        </Typography>
        <div style={{ background: '#f5f7f8', border: '1px solid #e1e6ea', borderRadius: 6, padding: 12, fontSize: 12, color: tokens.text.secondary }}>
          <div style={{ marginBottom: 6 }}><strong style={{ color: tokens.text.primary }}>Invoice:</strong> {item?.data?.invoiceNumber || '\u2014'}</div>
          <div style={{ marginBottom: 6 }}><strong style={{ color: tokens.text.primary }}>Amount:</strong> {formatCurrency(item?.data?.finalAmount ?? item?.data?.amount)}</div>
          <div><strong style={{ color: tokens.text.primary }}>Receipt:</strong> {receiptId ? `#${receiptId}` : 'Not pushed'}</div>
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 18px', gap: 8 }}>
        <Button onClick={onClose} size="small">Close</Button>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          href={receiptUrl || undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!receiptUrl}
          startIcon={<OpenInNewIcon />}
        >
          Receipt
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          href={workTicketUrl || undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!workTicketUrl}
          startIcon={<OpenInNewIcon />}
        >
          Work Ticket
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ReviewDetailPanel = ({ item, url, onViewInQueue, onItemCompleted }) => {
  const [completeCycleOpen, setCompleteCycleOpen] = useState(false)
  const [completeCycleLoading, setCompleteCycleLoading] = useState(false)
  const [completeCycleError, setCompleteCycleError] = useState('')
  const [reconciliationLoading, setReconciliationLoading] = useState(false)
  const [reconciliationError, setReconciliationError] = useState('')
  const [invoiceManualApprovalOpen, setInvoiceManualApprovalOpen] = useState(false)
  const [invoiceApprovalLoading, setInvoiceApprovalLoading] = useState(false)
  const [invoiceApprovalError, setInvoiceApprovalError] = useState('')

  const handleCompleteCycle = useCallback(async (scopeAction) => {
    if (!item) return
    setCompleteCycleLoading(true)
    setCompleteCycleError('')
    try {
      await axios.put(`${url}/api/herbicide-schedule/${item.scheduleItemId}/complete-cycle`, { scopeAction })
      setCompleteCycleOpen(false)
      if (onItemCompleted) onItemCompleted(item)
    } catch (err) {
      console.error('Error completing cycle:', err)
      setCompleteCycleError(err.response?.data?.error || 'Failed to complete cycle.')
    }
    setCompleteCycleLoading(false)
  }, [item, url, onItemCompleted])

  const handleResolveReconciliation = useCallback(async (status) => {
    const eventId = item?.data?.event?._id
    if (!eventId) return
    setReconciliationLoading(true)
    setReconciliationError('')
    try {
      await axios.put(`${url}/api/herbicide-schedule/reconciliation-events/${eventId}`, { status })
      if (onItemCompleted) onItemCompleted(item)
    } catch (err) {
      console.error('Error updating reconciliation event:', err)
      setReconciliationError(err.response?.data?.error || 'Failed to update reconciliation event.')
    }
    setReconciliationLoading(false)
  }, [item, url, onItemCompleted])

  const handlePromoteGeometry = useCallback(async () => {
    const event = item?.data?.event || {}
    const geometryId = event.sprayGeometryId?.geometryId
    if (!geometryId) return
    setReconciliationLoading(true)
    setReconciliationError('')
    try {
      await axios.post(`${url}/api/herbicide-schedule/spray-geometries/${encodeURIComponent(geometryId)}/promote`, {
        name: event.sprayGeometryId?.proposalGroupLabel || event.areaId?.name || '',
        note: event.note || ''
      })
      if (onItemCompleted) onItemCompleted(item)
    } catch (err) {
      console.error('Error promoting spray geometry:', err)
      setReconciliationError(err.response?.data?.error || 'Failed to promote spray geometry.')
    }
    setReconciliationLoading(false)
  }, [item, url, onItemCompleted])

  const handleApproveInvoice = useCallback(async () => {
    const invoiceId = item?.data?.invoiceId
    if (!invoiceId) return
    if (!ENABLE_AUTOMATED_INVOICE_APPROVAL) {
      setInvoiceApprovalError('')
      setInvoiceManualApprovalOpen(true)
      return
    }
    setInvoiceApprovalLoading(true)
    setInvoiceApprovalError('')
    try {
      await axios.put(`${url}/api/subcontractor-invoices/${invoiceId}/approve`)
      if (onItemCompleted) onItemCompleted(item)
    } catch (err) {
      console.error('Error approving subcontractor invoice:', err)
      setInvoiceApprovalError(err.response?.data?.message || err.response?.data?.error || 'Failed to approve invoice.')
    }
    setInvoiceApprovalLoading(false)
  }, [item, url, onItemCompleted])

  if (!item) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tokens.text.muted }}>
        <Typography variant="body2">Select an item to view details</Typography>
      </div>
    )
  }

  const DetailRenderer = detailRenderers[item.category]
  const color = categoryColors[item.category] || '#666'

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#fafbfc' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ background: color, color: '#fff', fontSize: 9, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>
          {categoryLabels[item.category]}
        </span>
        <span
          style={{ fontSize: 11, color: tokens.accent.blue, cursor: 'pointer', textDecoration: 'underline', marginLeft: 'auto' }}
          onClick={() => onViewInQueue(item.scheduleItemId)}
        >
          View in Queue &rarr;
        </span>
      </div>
      <Typography variant="h6" style={{ margin: '0 0 4px', fontSize: 18, color: tokens.text.primary }}>{item.siteName}</Typography>
      <Typography variant="body2" style={{ margin: '0 0 20px', color: tokens.text.secondary, fontSize: 12 }}>{item.address}</Typography>

      {DetailRenderer && <DetailRenderer data={item.data} />}
      {reconciliationError && (
        <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>{reconciliationError}</Typography>
      )}
      {invoiceApprovalError && (
        <Typography variant="body2" color="error" style={{ marginBottom: 12 }}>{invoiceApprovalError}</Typography>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        {item.category === 'needs_review' && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={() => setCompleteCycleOpen(true)}
          >
            Complete Cycle
          </Button>
        )}
        {item.category === 'geometry_reconciliation' && (
          <>
            {item.data?.event?.sprayGeometryId?.geometryId && item.data?.event?.sprayGeometryId?.validationSeverity !== 'reject' && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={reconciliationLoading}
                onClick={handlePromoteGeometry}
              >
                Promote to Area
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              disabled={reconciliationLoading}
              onClick={() => handleResolveReconciliation('resolved')}
            >
              Resolve
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={reconciliationLoading}
              onClick={() => handleResolveReconciliation('dismissed')}
            >
              Dismiss
            </Button>
          </>
        )}
        {item.category === 'sub_invoices' && item.data?.invoiceId && (
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={invoiceApprovalLoading}
              onClick={handleApproveInvoice}
            >
              {invoiceApprovalLoading ? 'Approving...' : 'Approve Invoice'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              href={`/hmi/subcontractor-invoices?invoiceId=${encodeURIComponent(item.data.invoiceId)}`}
            >
              Open Invoice
            </Button>
            {item.data?.workTicket?.workTicketId && (
              <Button
                variant="outlined"
                size="small"
                href={buildAspireWorkTicketUrl(item.data.workTicket.workTicketId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Work Ticket
              </Button>
            )}
          </>
        )}
        <Button
          variant="outlined"
          size="small"
          disabled={!item.scheduleItemId || String(item.scheduleItemId).startsWith('event:')}
          onClick={() => onViewInQueue(item.scheduleItemId)}
        >
          View in Queue
        </Button>
      </div>

      <CompleteCycleDialog
        open={completeCycleOpen}
        loading={completeCycleLoading}
        error={completeCycleError}
        onClose={() => setCompleteCycleOpen(false)}
        onComplete={handleCompleteCycle}
      />
      <InvoiceManualApprovalDialog
        open={invoiceManualApprovalOpen}
        item={item}
        onClose={() => setInvoiceManualApprovalOpen(false)}
      />
    </div>
  )
}

export default ReviewDetailPanel
